import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@/components/layout';
import { HistoricalTable } from '@/components/business';
import { ELOChart } from '@/components/charts';
import { getTopHistoricalPlayers, loadEloHistory } from '@/utils/dataLoader';
import type { HistoricalPlayer } from '@/types';

const LegendPage: React.FC = () => {
  const navigate = useNavigate();
  const [historicalPlayers, setHistoricalPlayers] = useState<
    HistoricalPlayer[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState<{
    datasets: Array<{
      label: string;
      data: Array<{
        x: number;
        y: number;
      }>;
      borderColor: string;
      backgroundColor: string;
      tension: number;
    }>;
  } | null>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [players, eloHistory] = await Promise.all([
          getTopHistoricalPlayers(500),
          loadEloHistory(),
        ]);

        setHistoricalPlayers(players);

        // 颜色管理函数
        const getLineColor = (index: number) => {
          const baseColors = [
            'rgb(201, 170, 113)', // glory-gold
            'rgb(59, 130, 246)', // blue-500
            'rgb(239, 68, 68)', // red-500
            'rgb(34, 197, 94)', // green-500
            'rgb(168, 85, 247)', // purple-500
            'rgb(245, 158, 11)', // amber-500
            'rgb(236, 72, 153)', // pink-500
            'rgb(20, 184, 166)', // teal-500
            'rgb(251, 146, 60)', // orange-500
            'rgb(139, 69, 19)', // brown
          ];
          return baseColors[index] || `hsl(${(index * 137.5) % 360}, 70%, 50%)`;
        };

        // 为前10名选手准备图表数据
        if (players.length > 0) {
          const top10Players = players.slice(0, 5);

          const datasets = top10Players
            .map((player, index) => {
              const playerHistory = eloHistory[player.id.toString()] || [];

              if (playerHistory.length === 0) return null;

              return {
                label: player.playerId,
                data: playerHistory.map(record => ({
                  x: new Date(record.date).getTime(),
                  y: record.eloRating,
                })),
                borderColor: getLineColor(index),
                backgroundColor: getLineColor(index) + '20',
                tension: 0.4,
              };
            })
            .filter(dataset => dataset !== null);

          setChartData({
            datasets,
          });
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('加载历史最高ELO数据失败:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const handlePlayerClick = (player: HistoricalPlayer) => {
    navigate(`/player/${player.id}`);
  };

  if (loading) {
    return (
      <Layout currentPath='/legends'>
        <div className='flex justify-center items-center h-64'>
          <div className='text-lg text-text-secondary'>加载中...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout currentPath='/legends'>
      <div className='space-y-8'>
        {/* 页面标题 */}
        <div className='text-center'>
          <h1 className='text-3xl font-bold text-text-primary mb-2'>
            历史最强选手排名
          </h1>
          <p className='text-text-secondary'>
            记录历史上ELO分数最高的选手以及他们的传奇表现
          </p>
        </div>

        {/* ELO变化对比图表区域 */}
        {chartData && (
          <div className='space-y-4'>
            <h2 className='text-xl font-semibold text-text-primary flex items-center gap-2'>
              <span className='w-1 h-6 bg-glory-gold rounded'></span>
              顶级选手ELO变化对比
            </h2>
            <div className='bg-background-secondary rounded-xl p-6 border border-border'>
              <ELOChart data={chartData} height={400} showPoints={false} />
            </div>
          </div>
        )}

        {/* 历史最高ELO表格区域 */}
        <div className='space-y-4'>
          <h2 className='text-xl font-semibold text-text-primary flex items-center gap-2'>
            <span className='w-1 h-6 bg-glory-gold rounded'></span>
            历史最高ELO记录 Top {historicalPlayers.length}
          </h2>
          <HistoricalTable
            data={historicalPlayers}
            onRowClick={handlePlayerClick}
          />
        </div>

        {/* 统计信息 */}
        <div className='bg-background-secondary rounded-xl p-6 border border-border'>
          <div className='grid grid-cols-1 md:grid-cols-3 gap-6 text-center'>
            <div className='space-y-1'>
              <div className='text-2xl font-bold text-text-primary'>
                {historicalPlayers.length > 0
                  ? historicalPlayers[0].peakElo.toLocaleString()
                  : 'N/A'}
              </div>
              <div className='text-sm text-text-secondary'>历史最高ELO</div>
            </div>
            <div className='space-y-1'>
              <div className='text-2xl font-bold text-text-primary'>
                {historicalPlayers.length > 0
                  ? new Date(historicalPlayers[0].peakDate).toLocaleDateString(
                      'zh-CN'
                    )
                  : 'N/A'}
              </div>
              <div className='text-sm text-text-secondary'>记录创造日期</div>
            </div>
            <div className='space-y-1'>
              <div className='text-2xl font-bold text-text-primary'>
                {historicalPlayers.length}
              </div>
              <div className='text-sm text-text-secondary'>历史传奇选手</div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default LegendPage;
